import { Component, EventEmitter, Input, Output } from '@angular/core';
import { actionCredits } from 'src/app/services/operators/voyageradar-credit-actions';
import { voyageSensorSpendGate, voyageSpendBlocker } from '../../voyage-credits.model';

/** One rendered history row — a normalised view over a `vesselHistory` record. */
export interface VesselHistoryRowView {
  mmsi: string;
  label: string;
  isNamed: boolean;
  /** "12 min ago" — age of the stored fix, or null without a stamp. */
  ago: string | null;
  /** Fix older than 48 h — captioned "last known", hollow glyph. */
  stale: boolean;
  /** Destination from the last lookup, when one has run. */
  destination: string | null;
  hasFix: boolean;
  /** The raw vesselHistory record — what the outputs emit. */
  data: any;
}

/**
 * Voyage Radar "Saved Vessels" — every vessel the user has located, whether from
 * a worldwide search or a popup's header buttons on a snapshot hull, persisted on
 * the sensor (`vesselHistory`, newest 50).
 *
 * The fifth panel section, sibling of Vessel Tracks and shaped like it. Where
 * that one answers "which vessels has this task bought a PATH for", this one
 * answers "which vessels has this task kept" — automatically: locating a
 * vessel is what writes a row, there is no separate save act. Rows are
 * LIST-ONLY until their Locate is clicked (fifty rows must not carpet the
 * chart in markers), and Refresh re-runs the paid by-MMSI lookup for a fresh
 * fix plus destination/details.
 *
 * The last two actions are deliberately separate, where a single ✕ used to do
 * both: `layers_clear` takes the MARKER off the map and leaves the row exactly
 * as it was (a click on Locate puts it back), while the bin DELETES the row for
 * good, here and on the server. Tidying the chart and giving up on a vessel are
 * different decisions, and only one of them can be undone.
 *
 * Purely presentational, like its siblings: it knows nothing about Leaflet or
 * the API. The host wires the outputs to the map controller and feeds back
 * `onMapMmsis`/`loadingMmsi`, so the section and the map can never disagree.
 *
 * Change detection stays Default to match its siblings: the host mutates
 * `sensor.vesselHistory` in place when a locate or lookup lands.
 */
@Component({
  selector: 'app-voyage-vessel-history',
  templateUrl: './voyage-vessel-history.component.html',
  styleUrls: ['./voyage-vessel-history.component.scss'],
})
export class VoyageVesselHistoryComponent {
  /** The Voyage Radar sensor whose `vesselHistory` this renders. */
  @Input() sensor: any;
  /** MMSIs currently drawn as located markers — the "on map" tag. */
  @Input() onMapMmsis: ((mmsi: string) => boolean) | null = null;
  /** MMSI of the lookup in flight, or null — spins that row's Refresh. */
  @Input() loadingMmsi: string | null = null;

  /** Draw/centre this vessel on the map (free). Emits the raw record. */
  @Output() locate = new EventEmitter<any>();
  /** Re-run the paid by-MMSI lookup for this vessel. Emits the raw record. */
  @Output() refresh = new EventEmitter<any>();
  /**
   * Take this vessel's marker off the map, keeping the row. Reversible — the
   * row's Locate puts it straight back. Emits the raw record.
   */
  @Output() takeOffMap = new EventEmitter<any>();
  /**
   * Delete this vessel from Saved Vessels for good (and off the map with it).
   * The one destructive act in the row. Emits the raw record.
   */
  @Output() remove = new EventEmitter<any>();

  /** Expanded by default, like Vessel Tracks: short when empty, and the list
   *  IS the feature. */
  expanded = true;

  private _cacheKey = '';
  private _rows: VesselHistoryRowView[] = [];

  private static readonly STALE_FIX_MS = 48 * 3600 * 1000;

  get history(): any[] {
    return Array.isArray(this.sensor?.vesselHistory) ? this.sensor.vesselHistory : [];
  }

  /** Live catalogue price for the row's Refresh — an ee-admin re-price sticks. */
  get lookupCost(): number {
    // `actionCredits` rather than a hand-rolled normalize-find-fallback: it is
    // the same three steps, and the copy here could drift from the shipped
    // fallback the rest of the app prices with.
    return actionCredits(this.sensor, 'lookup');
  }

  /**
   * What the row's Refresh must go DEAD for, or null when it stays live — an
   * empty wallet, and deliberately NOT an ended TOI, which stays pressable so
   * the click reaches `refreshVesselLookup` and is answered with the modal. See
   * `voyageSpendBlocker`.
   *
   * Through the shared ladder rather than a local balance read: this panel used
   * to parse the wallet itself with `apiCreditsRemaining ?? apiCredits`, which
   * is not the contract the rest of the app holds — `??` keeps a NaN from the
   * fallback write path, where `voyageCreditsRemaining` falls past it, so a
   * sensor with no entitlement read as an unknown wallet here and as a real one
   * everywhere else.
   */
  get blocker(): string | null {
    return voyageSpendBlocker(voyageSensorSpendGate(this.sensor, 'lookup', this.lookupCost));
  }

  /**
   * Rows, newest fetch first, memoised like the Vessel Tracks groups: the
   * template reads this several times per change-detection pass and the host
   * repaints often while anything on the map moves. The key carries each row's
   * fetch stamp because a lookup refreshes a record IN PLACE — length alone
   * would serve a stale row after every Refresh.
   */
  get rows(): VesselHistoryRowView[] {
    const history = this.history;
    const key = history.map((r: any) => `${r?.mmsi}@${r?.name ?? ''}`).join('|');
    if (key === this._cacheKey) return this._rows;
    this._cacheKey = key;

    const now = Date.now();
    this._rows = history
      .map((rec: any): VesselHistoryRowView | null => {
        const mmsi = String(rec?.mmsi ?? '').trim();
        if (!mmsi) return null;
        const identity = rec?.identity ?? {};
        const asOf = rec?.asOf ?? {};
        const details = rec?.details ?? {};
        const shipName = identity.shipName ?? null;
        const t = new Date(asOf.time_utc ?? '').getTime();
        return {
          mmsi,
          label: shipName || `MMSI ${mmsi}`,
          isNamed: !!shipName,
          ago: this.relAge(t, now),
          stale: isNaN(t) || now - t > VoyageVesselHistoryComponent.STALE_FIX_MS,
          destination: details.destination ?? details.matchedDestination ?? null,
          hasFix: asOf.latitude != null && asOf.longitude != null,
          data: rec,
        };
      })
      .filter((r): r is VesselHistoryRowView => r !== null)
      .reverse(); // stored oldest→newest; the panel reads newest first
    return this._rows;
  }

  private relAge(t: number, now: number): string | null {
    if (isNaN(t)) return null;
    const mins = Math.max(0, Math.round((now - t) / 60000));
    if (mins < 60) return `${mins} min ago`;
    if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} d ago`;
  }

  isOnMap(row: VesselHistoryRowView): boolean {
    return this.onMapMmsis ? this.onMapMmsis(row.mmsi) : false;
  }

  isLoading(row: VesselHistoryRowView): boolean {
    return this.loadingMmsi === row.mmsi;
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  /** Row click = Locate (draw/centre) — the row's primary act, like a track row's play. */
  pick(row: VesselHistoryRowView): void {
    this.locate.emit(row.data);
  }

  locateRow(row: VesselHistoryRowView, event: Event): void {
    event.stopPropagation();
    this.locate.emit(row.data);
  }

  refreshRow(row: VesselHistoryRowView, event: Event): void {
    event.stopPropagation();
    // The BLOCKER, not the full refusal: an ended TOI has to reach the host so
    // the pre-flight there can raise the modal that names the refund.
    if (this.loadingMmsi || this.blocker) return;
    this.refresh.emit(row.data);
  }

  takeOffMapRow(row: VesselHistoryRowView, event: Event): void {
    event.stopPropagation();
    this.takeOffMap.emit(row.data);
  }

  removeRow(row: VesselHistoryRowView, event: Event): void {
    event.stopPropagation();
    this.remove.emit(row.data);
  }

  trackByMmsi(_i: number, row: VesselHistoryRowView): string {
    return row.mmsi;
  }
}
